import { useState, useEffect } from "react";
import { toast } from "sonner";
import { TrendingUp, Truck, Clock, Gauge, DollarSign, Star, AlertTriangle, Package, Plus, Pencil, Trash2, Fuel, Medal } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api, { zar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, useFetch, Spinner, StatCard } from "@/components/portal/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const COLORS = ["#DC2626", "#2563EB", "#16A34A", "#D97706", "#8B5CF6", "#F97316"];
const kmoney = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { maximumFractionDigits: 2 })}`;
const efErr = (e, fb) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);
const RANK = ["text-yellow-500", "text-slate-400", "text-amber-700"];

function FleetKpiSection() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "managing_director", "fleet_manager"].includes(user?.role);
  const [league, setLeague] = useState(null);
  const [entries, setEntries] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [lg, en, vh] = await Promise.all([
      api.get("/erp/fleet-kpis/league"),
      api.get("/erp/fleet-kpis"),
      api.get("/erp/vehicles").catch(() => ({ data: { vehicles: [] } })),
    ]);
    setLeague(lg.data.league || []);
    setEntries(en.data.fleet_kpis || []);
    setVehicles(vh.data.vehicles || []);
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({ vehicle_id: "", month: new Date().toISOString().slice(0, 7), distance_km: "", fuel_litres: "", target_cost_per_km: "", target_l_per_100km: "" });
  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ vehicle_id: e.vehicle_id, month: e.month, distance_km: e.distance_km, fuel_litres: e.fuel_litres, target_cost_per_km: e.target_cost_per_km ?? "", target_l_per_100km: e.target_l_per_100km ?? "" }); setOpen(true); };
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const submit = async () => {
    if (!form.vehicle_id) return toast.error("Vehicle is required");
    if (!form.month) return toast.error("Month is required");
    if (!form.distance_km || Number(form.distance_km) <= 0) return toast.error("Distance driven is required");
    const payload = { vehicle_id: form.vehicle_id, month: form.month, distance_km: Number(form.distance_km), fuel_litres: Number(form.fuel_litres || 0), target_cost_per_km: num(form.target_cost_per_km), target_l_per_100km: num(form.target_l_per_100km) };
    setBusy(true);
    try {
      if (editing) await api.patch(`/erp/fleet-kpis/${editing.id}`, payload);
      else await api.post("/erp/fleet-kpis", payload);
      toast.success(editing ? "Updated" : "Added"); setOpen(false); await load();
    } catch (e) { toast.error(efErr(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const del = async (e) => { if (!window.confirm("Delete this KPI entry?")) return; try { await api.delete(`/erp/fleet-kpis/${e.id}`); toast.success("Deleted"); await load(); } catch (err) { toast.error(efErr(err, "Delete failed")); } };

  if (!league) return <div className="mt-8"><Spinner /></div>;
  const byCost = [...league].filter((v) => v.distance_km > 0).sort((a, b) => a.cost_per_km - b.cost_per_km);
  const byFuel = [...league].filter((v) => v.l_per_100km > 0).sort((a, b) => a.l_per_100km - b.l_per_100km);
  const vname = (id) => (vehicles.find((v) => v.id === id)?.reg_no) || id;

  return (
    <div className="mt-10" data-testid="exec-fleet-kpis">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Gauge size={18} className="text-primary" /> Fleet performance league</h2>
        {canWrite && <Button onClick={openNew} className="bg-primary hover:bg-red-700" data-testid="fleet-kpi-add"><Plus size={16} className="mr-1.5" /> Add KPI entry</Button>}
      </div>

      {league.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground" data-testid="fleet-kpi-empty">
          No fleet KPI data yet. {canWrite ? "Add a vehicle's monthly distance & fuel to rank cost-per-km and fuel efficiency." : "A manager needs to log distance & fuel per vehicle."}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="fleet-league-cost">
            <div className="p-3 border-b border-border font-heading font-bold text-sm flex items-center gap-2"><TrendingUp size={15} className="text-primary" /> Cost per km (best → worst)</div>
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="p-3">#</th><th className="p-3">Vehicle</th><th className="p-3 text-right">Km</th><th className="p-3 text-right">Cost</th><th className="p-3 text-right">R/km</th><th className="p-3 text-right">Target</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {byCost.map((v, i) => (
                  <tr key={v.vehicle_id} className="hover:bg-muted/40" data-testid={`fleet-league-cost-row-${v.vehicle_id}`}>
                    <td className="p-3">{i < 3 ? <Medal size={16} className={RANK[i]} /> : i + 1}</td>
                    <td className="p-3"><div className="font-mono font-semibold">{v.vehicle_reg}</div><div className="text-xs text-muted-foreground">{v.branch}</div></td>
                    <td className="p-3 text-right text-muted-foreground">{v.distance_km.toLocaleString()}</td>
                    <td className="p-3 text-right text-muted-foreground">{kmoney(v.costs_zar)}</td>
                    <td className={`p-3 text-right font-bold ${v.cost_over_target ? "text-red-600" : "text-emerald-700 dark:text-emerald-400"}`}>{kmoney(v.cost_per_km)}</td>
                    <td className="p-3 text-right text-xs text-muted-foreground">{v.target_cost_per_km != null ? kmoney(v.target_cost_per_km) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="fleet-league-fuel">
            <div className="p-3 border-b border-border font-heading font-bold text-sm flex items-center gap-2"><Fuel size={15} className="text-primary" /> Fuel efficiency (best → worst)</div>
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr><th className="p-3">#</th><th className="p-3">Vehicle</th><th className="p-3 text-right">Km</th><th className="p-3 text-right">Litres</th><th className="p-3 text-right">L/100km</th><th className="p-3 text-right">Target</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {byFuel.map((v, i) => (
                  <tr key={v.vehicle_id} className="hover:bg-muted/40" data-testid={`fleet-league-fuel-row-${v.vehicle_id}`}>
                    <td className="p-3">{i < 3 ? <Medal size={16} className={RANK[i]} /> : i + 1}</td>
                    <td className="p-3"><div className="font-mono font-semibold">{v.vehicle_reg}</div><div className="text-xs text-muted-foreground">{v.branch}</div></td>
                    <td className="p-3 text-right text-muted-foreground">{v.distance_km.toLocaleString()}</td>
                    <td className="p-3 text-right text-muted-foreground">{v.fuel_litres.toLocaleString()}</td>
                    <td className={`p-3 text-right font-bold ${v.fuel_over_target ? "text-red-600" : "text-emerald-700 dark:text-emerald-400"}`}>{v.l_per_100km}</td>
                    <td className="p-3 text-right text-xs text-muted-foreground">{v.target_l_per_100km != null ? v.target_l_per_100km : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canWrite && entries.length > 0 && (
        <div className="mt-6 rounded-xl border border-border bg-card overflow-x-auto" data-testid="fleet-kpi-entries">
          <div className="p-3 border-b border-border font-heading font-bold text-sm">KPI entries (distance & fuel per vehicle/month)</div>
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="p-3">Month</th><th className="p-3">Vehicle</th><th className="p-3 text-right">Distance (km)</th><th className="p-3 text-right">Fuel (L)</th><th className="p-3 text-right">Target R/km</th><th className="p-3 text-right">Target L/100</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entries.map((e) => (
                <tr key={e.id} className="hover:bg-muted/40" data-testid={`fleet-kpi-row-${e.id}`}>
                  <td className="p-3 font-semibold">{e.month}</td>
                  <td className="p-3 font-mono">{e.vehicle_reg || vname(e.vehicle_id)}</td>
                  <td className="p-3 text-right">{Number(e.distance_km).toLocaleString()}</td>
                  <td className="p-3 text-right">{Number(e.fuel_litres).toLocaleString()}</td>
                  <td className="p-3 text-right text-muted-foreground">{e.target_cost_per_km != null ? kmoney(e.target_cost_per_km) : "—"}</td>
                  <td className="p-3 text-right text-muted-foreground">{e.target_l_per_100km != null ? e.target_l_per_100km : "—"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`fleet-kpi-edit-${e.id}`}><Pencil size={16} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(e)} data-testid={`fleet-kpi-del-${e.id}`} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"><Trash2 size={16} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="fleet-kpi-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} fleet KPI entry</DialogTitle><DialogDescription>Log distance driven and fuel used to rank cost-per-km and fuel efficiency. Targets are optional.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs">Vehicle *</Label>
              <select value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} disabled={!!editing} data-testid="fleet-kpi-f-vehicle" className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 text-sm disabled:opacity-60">
                <option value="">— select vehicle —</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{`${v.reg_no} · ${v.branch}`}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Month *</Label><Input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="fleet-kpi-f-month" className="mt-1" /></div>
            <div><Label className="text-xs">Distance driven (km) *</Label><Input type="number" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} data-testid="fleet-kpi-f-distance" className="mt-1" /></div>
            <div><Label className="text-xs">Fuel used (litres)</Label><Input type="number" value={form.fuel_litres} onChange={(e) => setForm({ ...form, fuel_litres: e.target.value })} data-testid="fleet-kpi-f-fuel" className="mt-1" /></div>
            <div><Label className="text-xs">Target cost / km (R)</Label><Input type="number" value={form.target_cost_per_km} onChange={(e) => setForm({ ...form, target_cost_per_km: e.target.value })} data-testid="fleet-kpi-f-tcpk" className="mt-1" /></div>
            <div><Label className="text-xs">Target L / 100km</Label><Input type="number" value={form.target_l_per_100km} onChange={(e) => setForm({ ...form, target_l_per_100km: e.target.value })} data-testid="fleet-kpi-f-tl" className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={submit} disabled={busy} className="bg-primary hover:bg-red-700 w-full" data-testid="fleet-kpi-save">{busy ? "Saving…" : editing ? "Save changes" : "Add entry"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Executive() {
  const { data, loading } = useFetch(() => api.get("/executive/kpis").then((r) => r.data));
  if (loading) return <Spinner />;
  const k = data.kpis;

  return (
    <div>
      <PageHeader title="Executive Dashboard" subtitle="Enterprise KPIs and performance analytics" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="kpi-shipments" label="Total Shipments" value={k.total_shipments} icon={Package} />
        <StatCard testid="kpi-revenue" label="Revenue" value={zar(k.revenue)} icon={DollarSign} accent="text-emerald-600" />
        <StatCard testid="kpi-ontime" label="On-Time Delivery" value={`${k.on_time_rate}%`} icon={Clock} accent="text-blue-600" />
        <StatCard testid="kpi-fleet" label="Fleet Utilisation" value={`${k.fleet_utilization}%`} icon={Truck} accent="text-orange-600" />
        <StatCard label="Vehicle Availability" value={`${k.vehicle_availability}%`} icon={Truck} />
        <StatCard label="Avg Delivery Time" value={`${k.avg_delivery_hours}h`} icon={Clock} />
        <StatCard label="Fuel Efficiency" value={`${k.fuel_efficiency} L`} icon={Gauge} sub="per 100km" />
        <StatCard label="Cost / Shipment" value={zar(k.cost_per_shipment)} icon={DollarSign} />
        <StatCard label="Cost / km" value={zar(k.cost_per_km)} icon={TrendingUp} />
        <StatCard label="Customer Satisfaction" value={`${k.customer_satisfaction}/5`} icon={Star} accent="text-yellow-600" />
        <StatCard label="Delayed Shipments" value={k.delayed_shipments} icon={AlertTriangle} accent="text-orange-600" />
        <StatCard label="Claims / Damages" value={k.claims_damages} icon={AlertTriangle} accent="text-red-600" />
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-heading font-bold mb-4">Top corridors by transport cost</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.charts.top_corridors} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis type="number" tickFormatter={(v) => `R${(v / 1000).toFixed(0)}k`} fontSize={11} />
              <YAxis type="category" dataKey="name" width={120} fontSize={10} />
              <Tooltip formatter={(v) => zar(v)} />
              <Bar dataKey="cost" fill="#DC2626" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-heading font-bold mb-4">Shipments by status</h2>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.charts.status_dist} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                {data.charts.status_dist.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <FleetKpiSection />
    </div>
  );
}
